import React, { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import styled from 'styled-components';
import { useAuth } from '../../utils/AuthContext';
import { isUserAdmin } from '../../utils/adminFunctions';

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100vh;
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  
  .spinner {
    width: 60px;
    height: 60px;
    border: 4px solid #eee;
    border-top: 4px solid #667eea;
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const AccessDenied = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 100vh;
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  text-align: center;
  padding: 2rem;
  
  .icon {
    font-size: 4rem;
    margin-bottom: 1rem;
  }
  
  h1 {
    color: #e74c3c;
    font-size: 2rem;
    margin-bottom: 1rem;
  }
  
  p {
    color: #666;
    font-size: 1.1rem;
    margin-bottom: 2rem;
    max-width: 500px;
  }
  
  .back-button {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    border: none;
    padding: 1rem 2rem;
    border-radius: 10px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;
    text-decoration: none;
    display: inline-block;
    
    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 5px 15px rgba(0, 0, 0, 0.2);
    }
  }
`;

interface ProtectedAdminRouteProps {
  children: React.ReactNode;
}

const ProtectedAdminRoute: React.FC<ProtectedAdminRouteProps> = ({ children }) => {
  const { user, loading: authLoading } = useAuth();
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkAdminStatus = async () => {
      if (!user) {
        setIsAdmin(false);
        setLoading(false);
        return;
      }

      try {
        const adminStatus = await isUserAdmin();
        setIsAdmin(adminStatus);
      } catch (error) {
        console.error('Errore verifica admin:', error);
        setIsAdmin(false);
      } finally {
        setLoading(false);
      }
    };

    if (!authLoading) {
      checkAdminStatus();
    }
  }, [user, authLoading]);

  // Loading state
  if (authLoading || loading) {
    return (
      <LoadingContainer>
        <div>
          <div className="spinner"></div>
          <p style={{ marginTop: '1rem', color: '#666' }}>
            Verifica permessi amministratore...
          </p>
        </div>
      </LoadingContainer>
    );
  }

  // Not authenticated
  if (!user) {
    return <Navigate to="/admin/login" replace />;
  }

  // Not admin
  if (!isAdmin) {
    return (
      <AccessDenied>
        <div className="icon">🚫</div>
        <h1>Accesso Negato</h1>
        <p>
          Non hai i permessi necessari per accedere al pannello amministrativo.
          Solo gli amministratori autorizzati possono visualizzare questa sezione.
        </p>
        <a href="/" className="back-button">
          🏠 Torna alla Home
        </a>
      </AccessDenied>
    );
  }

  // Admin authenticated
  return <>{children}</>;
};

export default ProtectedAdminRoute; 